import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, RefreshCw, Trash2, ExternalLink } from 'lucide-react';
import socialCredentialsService, {
  type SocialCredentialRow,
  type SocialProviderName,
} from 'api/services/socialCredentials.service';
import { BRAND_CONFIG } from 'constants/branding';

/**
 * Register this organisation's own Google / Microsoft 365 / ChatGPT application.
 *
 * <h3>Why a reseller would bother</h3>
 * The consent screen names the application asking for access. Without an application
 * of your own, your customers are asked to grant access to the platform operator — a
 * company they have never dealt with, on the one screen where people actually read
 * the name. The page says this plainly rather than presenting the fields without a
 * reason, because a setting whose purpose is not obvious does not get set.
 *
 * <h3>The secret field is always empty, and that is not a bug</h3>
 * The API never returns a stored secret — it reports only that one exists. So the
 * field loads blank every time, and leaving it blank KEEPS what is stored. Saying so
 * on the page matters: the alternative reading, that blank means "no secret", is what
 * would make somebody retype it on every edit, or worry that they had wiped it.
 */

/** Where each provider's applications are registered. */
const CONSOLES: Record<SocialProviderName, { url: string; label: string }> = {
  GOOGLE: { url: 'https://console.cloud.google.com/apis/credentials', label: 'Google Cloud console' },
  MICROSOFT: { url: 'https://entra.microsoft.com', label: 'Microsoft Entra admin centre' },
  OPENAI: { url: 'https://platform.openai.com', label: 'OpenAI platform' },
};

interface Draft {
  clientId: string;
  clientSecret: string;
  enabled: boolean;
}

const SocialSignIn: React.FC = () => {
  const [rows, setRows] = useState<SocialCredentialRow[]>([]);
  const [redirectUri, setRedirectUri] = useState('');
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [savingProvider, setSavingProvider] = useState<string | null>(null);
  const [note, setNote] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const settings = await socialCredentialsService.list();
      const list = settings.providers ?? [];
      setRows(list);
      setRedirectUri(settings.redirect_uri ?? '');
      const next: Record<string, Draft> = {};
      list.forEach((r) => {
        // The secret deliberately starts empty — it cannot be read back.
        next[r.provider] = { clientId: r.client_id ?? '', clientSecret: '', enabled: r.enabled };
      });
      setDrafts(next);
    } catch {
      setNote({ kind: 'err', text: 'Could not load sign-in applications.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = (provider: string, patch: Partial<Draft>) =>
    setDrafts((d) => ({ ...d, [provider]: { ...d[provider], ...patch } }));

  const save = async (row: SocialCredentialRow) => {
    const draft = drafts[row.provider];
    if (!draft?.clientId.trim()) {
      setNote({ kind: 'err', text: 'A client ID is required.' });
      return;
    }
    setSavingProvider(row.provider);
    setNote(null);
    try {
      const body: { client_id: string; client_secret?: string; enabled?: boolean } = {
        client_id: draft.clientId.trim(),
        enabled: draft.enabled,
      };
      // Only send a secret the user actually typed. Sending '' would erase the
      // stored one on every save, since the field can never be pre-filled.
      if (draft.clientSecret.trim()) body.client_secret = draft.clientSecret.trim();

      const result = await socialCredentialsService.save(row.provider, body);
      if (result?.success) {
        setNote({ kind: 'ok', text: `${row.display_name} sign-in saved.` });
        await load();
      } else {
        setNote({ kind: 'err', text: result?.message || 'Could not save that application.' });
      }
    } catch {
      setNote({ kind: 'err', text: 'Could not save that application.' });
    } finally {
      setSavingProvider(null);
    }
  };

  const remove = async (row: SocialCredentialRow) => {
    setSavingProvider(row.provider);
    setNote(null);
    try {
      await socialCredentialsService.remove(row.provider);
      setNote({
        kind: 'ok',
        text: `${row.display_name} application removed. The button now uses the platform's application.`,
      });
      await load();
    } catch {
      setNote({ kind: 'err', text: 'Could not remove that application.' });
    } finally {
      setSavingProvider(null);
    }
  };

  return (
    <div className="p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900">
            <KeyRound className="h-5 w-5 text-blue-700" aria-hidden="true" />
            Sign-in providers
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            Let people sign in to {BRAND_CONFIG.brandName} with Google, Microsoft 365 or ChatGPT.
            Register your own application with a provider and the consent screen your customers
            see will carry <strong>your</strong> name — without one, it carries the platform
            operator&rsquo;s.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" aria-hidden="true" />
          Refresh
        </button>
      </header>

      {note && (
        <div
          role="status"
          className={`mt-4 rounded-lg border px-3 py-2 text-sm ${
            note.kind === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {note.text}
        </div>
      )}

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((row) => {
            const draft = drafts[row.provider] ?? { clientId: '', clientSecret: '', enabled: false };
            const busy = savingProvider === row.provider;
            const console = CONSOLES[row.provider];

            return (
              <section
                key={row.provider}
                className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-slate-900">{row.display_name}</h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {row.configured ? (
                        <>
                          Your own application
                          {row.client_secret_set ? ', with a client secret stored' : ', no client secret'}
                          {row.enabled ? '' : ' — currently switched off'}
                        </>
                      ) : (
                        <>Not configured — the platform&rsquo;s application is used</>
                      )}
                    </p>
                  </div>

                  <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                      id={`enabled-${row.provider}`}
                      type="checkbox"
                      checked={draft.enabled}
                      onChange={(e) => update(row.provider, { enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                    />
                    Show this button
                  </label>
                </div>

                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor={`client-id-${row.provider}`}
                      className="block text-xs font-medium text-slate-700"
                    >
                      Client ID
                    </label>
                    <input
                      id={`client-id-${row.provider}`}
                      type="text"
                      value={draft.clientId}
                      onChange={(e) => update(row.provider, { clientId: e.target.value })}
                      placeholder="From your provider console"
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor={`client-secret-${row.provider}`}
                      className="block text-xs font-medium text-slate-700"
                    >
                      Client secret
                    </label>
                    <input
                      id={`client-secret-${row.provider}`}
                      type="password"
                      autoComplete="new-password"
                      value={draft.clientSecret}
                      onChange={(e) => update(row.provider, { clientSecret: e.target.value })}
                      placeholder={
                        row.client_secret_set ? 'Stored — leave blank to keep it' : 'Optional'
                      }
                      className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
                    />
                    <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
                      {row.client_secret_set
                        ? 'A secret is stored. We cannot show it back to you, so leave this blank unless you are replacing it.'
                        : 'Optional — sign-in uses PKCE, which does not need a client secret.'}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => save(row)}
                    className="inline-flex items-center rounded-lg bg-blue-700 px-3.5 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
                  >
                    {busy ? 'Saving…' : 'Save'}
                  </button>

                  {row.configured && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => remove(row)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                      Remove
                    </button>
                  )}

                  {console && (
                    <a
                      href={console.url}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-800"
                    >
                      {console.label}
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                    </a>
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {redirectUri && (
        <section className="mt-6 max-w-2xl rounded-xl border border-slate-200 bg-slate-50 p-4">
          <h2 className="text-sm font-semibold text-slate-900">Redirect URL</h2>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">
            Register this against every application above, exactly as written. Providers reject a
            mismatch, and the error appears on their sign-in page rather than here &mdash; so a
            stray slash is surprisingly hard to diagnose.
          </p>
          <code className="mt-2 block overflow-x-auto rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800">
            {redirectUri}
          </code>
        </section>
      )}
    </div>
  );
};

export default SocialSignIn;
